// Zebra Expr — aggregate decomposition (Formula Store, Step 3.1).
//
// The Formula Store wants to maintain a folder's rollup value INCREMENTALLY:
// when one issue changes, adjust the stored result by a delta instead of
// re-walking the whole (up to 40k-node) tree. That's only possible if we store
// the aggregation COMPONENTS — the raw SUM/COUNT accumulators — rather than the
// finished "% Overdue" ratio, because a ratio can't be delta'd but a SUM can
// (new = old + (newLeafValue - oldLeafValue)).
//
// This module splits a formula into (a) its aggregation nodes — the parts that
// walk the tree — and (b) the surrounding pure arithmetic/formatting. Store each
// aggregation's value; re-derive the display value any time by substituting the
// stored values back in (evalWithAggValues). No tree walk on read.
//
// It changes NO existing behavior: purely additive helpers over the same AST.

import { parse } from './parser.js';
import { interpret } from './interpreter.js';

// Aggregates whose result can be maintained from stored components alone:
//  SUM   — additive: result += (newLeaf - oldLeaf)
//  COUNT — additive: result += (newTruthy - oldTruthy)
//  AVG   — maintainable if we also keep the count (sum/count both additive)
// MIN/MAX are semi (a removed extreme needs a re-walk); JOIN/MEDIAN/VALUES/ARRAY
// aren't decomposable. Non-decomposable aggregates still get a stored value —
// they just fall back to a full recompute on change instead of a delta.
const DECOMPOSABLE = new Set(['SUM', 'COUNT', 'AVG', 'AVERAGE']);

// Child AST nodes of `node`, in evaluation order. MUST match the order in which
// map() (below) rebuilds them, so the pre-order aggregate index is identical
// between extractAggregates and evalWithAggValues. Leaf kinds (num/str/bool/
// null/field/lit) have no child nodes.
function children(node) {
  switch (node.kind) {
    case 'prop': return [node.object];
    case 'unary': return [node.operand];
    case 'binary': return [node.left, node.right];
    case 'if': return node.else == null ? [node.cond, node.then] : [node.cond, node.then, node.else];
    case 'with': return [node.value, node.body];
    case 'withfn': return [node.value, node.body];
    case 'template': return node.parts;
    case 'call': return node.args;
    case 'lambda': return [node.body];
    // NOTE: 'agg' is intentionally NOT recursed into here — an aggregate is a
    // decomposition boundary (its body is walked as one unit). A nested agg in
    // an agg body is subsumed by the outer one, not maintained independently.
    default: return [];
  }
}

// List every top-level aggregation node in the formula, in a stable pre-order.
// Each entry: { index, name, scope, body, node, decomposable }
//   index        — 0-based pre-order position; the stable id used to store this
//                  component (component_index in the store)
//   name         — the reduce op (SUM/COUNT/AVG/...)
//   scope        — { type, extra } (subtree/leaves/children/all)
//   body         — the per-item sub-expression AST (evaluated per leaf)
//   node         — the raw agg AST node (interpret() it directly for its value)
//   decomposable — true when the value can be delta-maintained (see DECOMPOSABLE)
export function extractAggregates(astOrSource) {
  const ast = typeof astOrSource === 'string' ? parse(astOrSource) : astOrSource;
  const out = [];
  (function walk(n) {
    if (!n || typeof n !== 'object' || !n.kind) return;
    if (n.kind === 'agg') {
      out.push({
        index: out.length,
        name: n.name,
        scope: n.scope || {},
        body: n.body,
        node: n,
        decomposable: DECOMPOSABLE.has(n.name),
      });
      return; // boundary — don't descend into the aggregate body
    }
    for (const c of children(n)) walk(c);
  })(ast);
  return out;
}

// Re-derive the formula's value using PRECOMPUTED aggregate values (by pre-order
// index, matching extractAggregates) instead of walking the tree. Each agg node
// is replaced by a literal carrying its stored value; the rest of the AST (the
// arithmetic, IFs, panel formatting) evaluates normally. rootItem supplies any
// non-aggregate field lookups the surrounding expression makes (usually none for
// a pure rollup — pass an empty item).
export function evalWithAggValues(astOrSource, aggValues, rootItem) {
  const ast = typeof astOrSource === 'string' ? parse(astOrSource) : astOrSource;
  let i = 0;
  const map = (n) => {
    if (!n || typeof n !== 'object' || !n.kind) return n;
    if (n.kind === 'agg') return { kind: 'lit', value: aggValues[i++] };
    const c = { ...n };
    switch (n.kind) {
      case 'prop': c.object = map(n.object); break;
      case 'unary': c.operand = map(n.operand); break;
      case 'binary': c.left = map(n.left); c.right = map(n.right); break;
      case 'if': c.cond = map(n.cond); c.then = map(n.then); if (n.else != null) c.else = map(n.else); break;
      case 'with': c.value = map(n.value); c.body = map(n.body); break;
      case 'withfn': c.value = map(n.value); c.body = map(n.body); break;
      case 'template': c.parts = n.parts.map(map); break;
      case 'call': c.args = n.args.map(map); break;
      case 'lambda': c.body = map(n.body); break;
      default: break; // leaf
    }
    return c;
  };
  return interpret(map(ast), rootItem || { fields: {}, children: [] });
}
